import * as turf from '@turf/turf';
import type { Feature, Polygon as GJPolygon } from 'geojson';
import { HEAVY_FEATURE_LIMIT, HEAVY_VERTEX_LIMIT, INTERSECTION_AREA_EPS } from './map-editor.constants';
import { holeDefect, isClockwise } from './map-geometry.utils';

type Ring = [number, number][];

export function overlapAreaM2(a: turf.AllGeoJSON, b: turf.AllGeoJSON): number {
  try {
    if (!turf.booleanIntersects(a as any, b as any)) return 0;
    const intersection = intersectionOf(a, b);
    if (intersection) return Math.max(0, turf.area(intersection as any));
    return containedAreaM2(a, b);
  } catch {
    return 0;
  }
}

function intersectionOf(a: turf.AllGeoJSON, b: turf.AllGeoJSON) {
  try {
    return turf.intersect(turf.featureCollection([a, b] as any));
  } catch {
    return null;
  }
}

function containedAreaM2(a: turf.AllGeoJSON, b: turf.AllGeoJSON): number {
  const t = turf as any;
  const containsAB = t.booleanContains ? t.booleanContains(a, b) : false;
  const containsBA = t.booleanContains ? t.booleanContains(b, a) : false;
  const withinAB = t.booleanWithin ? t.booleanWithin(a, b) : false;
  const withinBA = t.booleanWithin ? t.booleanWithin(b, a) : false;
  if (containsAB || withinBA) return turf.area(b as any);
  if (containsBA || withinAB) return turf.area(a as any);
  return 0;
}

export function polygonStats(polys: Feature<GJPolygon>[]) {
  let vertexCount = 0;
  for (const poly of polys) {
    for (const ring of (poly.geometry?.coordinates ?? []) as Ring[]) vertexCount += ring?.length ?? 0;
  }
  return { featureCount: polys.length, vertexCount };
}

export function largeDatasetReason(polys: Feature<GJPolygon>[]): string | null {
  const { featureCount, vertexCount } = polygonStats(polys);
  if (featureCount > HEAVY_FEATURE_LIMIT) {
    return `Dataset has ${featureCount} polygons; skipping automatic highlighting to keep the map responsive.`;
  }
  if (vertexCount > HEAVY_VERTEX_LIMIT) {
    return `Dataset has about ${vertexCount} vertices; skipping automatic highlighting to keep the map responsive.`;
  }
  return null;
}

export function findPolygonErrors(polys: Feature<GJPolygon>[]): string[] {
  return [...holeErrors(polys), ...selfIntersectionErrors(polys), ...overlapErrors(polys), ...orientationErrors(polys)];
}

function holeErrors(polys: Feature<GJPolygon>[]): string[] {
  return polys.flatMap((poly, i) =>
    holeDefect(poly) ? [`Polygon ${i + 1} has a hole outside or across its outline.`] : []
  );
}

function selfIntersectionErrors(polys: Feature<GJPolygon>[]): string[] {
  const errors: string[] = [];
  polys.forEach((poly, i) => {
    if (holeDefect(poly)) return;
    try {
      const count = (turf.kinks(poly as any) as any)?.features?.length ?? 0;
      if (count > 0) errors.push(`Polygon ${i + 1} has ${count} self-intersection(s).`);
    } catch {}
  });
  return errors;
}

function overlapErrors(polys: Feature<GJPolygon>[]): string[] {
  const errors: string[] = [];
  for (let i = 0; i < polys.length; i++) {
    for (let j = i + 1; j < polys.length; j++) {
      const area = overlapAreaM2(polys[i] as any, polys[j] as any);
      if (area > INTERSECTION_AREA_EPS)
        errors.push(`Areas ${i + 1} and ${j + 1} overlap (area ~ ${area.toFixed(2)} m²).`);
    }
  }
  return errors;
}

function orientationErrors(polys: Feature<GJPolygon>[]): string[] {
  const errors: string[] = [];
  polys.forEach((poly, i) => {
    const rings = (poly.geometry.coordinates ?? []) as Ring[];
    if (!rings.length) return;
    if (!isClockwise(rings[0])) errors.push(`Polygon ${i + 1} outer ring is not clockwise (left-hand rule).`);
    for (let r = 1; r < rings.length; r++)
      if (isClockwise(rings[r])) errors.push(`Polygon ${i + 1} hole #${r} is not counter-clockwise (left-hand rule).`);
  });
  return errors;
}
